/** `lander/tranquility-nine` — the lander family template authored from the
 * stored Tranquility Nine concept: a fragile descent module drops down a
 * short portrait column under lunar gravity with no air drag. Left/right
 * roll it in place (free); the main engine pushes along the nose and burns a
 * fixed tank. The ground is a seeded jagged rock profile one screen wide,
 * broken by flat pads that flash their multiplier (×2 on the always-present
 * wide safety deck over the drop column, ×3/×5/×8 as decks narrow). Touch
 * down slow, level and with both feet on a deck to bank multiplier ×
 * softness points plus a fuel refund; anything else breaks the module apart
 * and costs one of two lives. Each landing settles, then a new field is
 * generated with decks 15% narrower than the last, and the module drops
 * again with the fuel it has left. The follow camera tracks the module down
 * the column.
 *
 * The concept's decisive end is kept: bank three landings to win (the live
 * game had drifted to endless-until-two-crashes); a second crash loses.
 * Constants follow the live `tranquility-nine` sim (constants.ts, field.ts). */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { TemplateV1 } from './template.js';

const W = 9, H = 20;
/** The descent module's box; its feet hang FEET_DROP below its centre. */
const MODULE = { w: 0.85, h: 0.95 };
const FEET_DROP = 0.5;
/** Where a descent begins (the module's centre), above the rock. */
const SPAWN = { x: 4.5, centreY: 3 };
const FUEL = 140;
/** Fuel burned per tick of main engine (7 units a second). */
const BURN_PER_TICK = 7 / 30;
const GOAL = 'landing-banked';

const globals: ModuleUseV1[] = [
  { use: 'flow.director', config: { phases: [{ kind: 'goal', spawns: [], durationTicks: 0, clearBonus: 0, layout: [] }], loopFrom: 0,
    interPhaseTicks: 20, requiredTag: 'required', collectTag: 'pickup', sweepTag: 'debris', goalSignal: GOAL } },
  // Each new field's decks are 15% narrower than the last one's.
  { use: 'flow.escalation', config: { curves: [{ scalar: 'pad.scale', index: 'phase.number', kind: 'geometric', base: 1, rate: 0.85, min: 0.3, max: 1 }] } },
  { use: 'world.heightfield', config: {
    cells: 18, base: 16.5, peak: 13.5, floor: 17, jitter: 1.1, pull: 0.2,
    safetyPad: { cells: 6, mult: 2 }, safeX: SPAWN.x, count: 3, pool: [{ cells: 4, mult: 3 }, { cells: 3, mult: 5 }, { cells: 2, mult: 8 }],
    gapCells: 2, rampDrop: 0.6, pad: 'landing-pad', padHeight: 0.4, minPadWidth: 0.75, widthScalar: 'pad.scale', regenerateOn: 'phase-started',
    fill: 3, edge: 2,
  } },
  // One fixed tank: the engine spends it every tick it burns, a banked landing refunds a little.
  { use: 'rule.resource', key: 'fuel', config: {
    label: 'FUEL', display: 'percent', capacity: FUEL, drainPerTick: 0, startRunning: true, fillOn: [],
    adjust: [{ signal: 'thrust', fromTag: 'lander', amount: -BURN_PER_TICK }, { signal: 'landed', fromTag: 'lander', amount: 20 }],
    cashOutOn: [], cashOutPoints: 0, onEmpty: 'none', scalar: 'fuel.level',
  } },
  { use: 'fx.debris', config: { on: 'damage', fromTag: 'lander', archetype: 'debris-shard', count: 8, speed: 3.5 } },
  // The concept's decisive end: bank three landings to win.
  { use: 'flow.objective', config: { win: 'phases-cleared', count: 3, milestone: 0, lose: 'players-out', requiredTag: 'required', collectTag: 'pickup', celebrate: true } },
];

export const TRANQUILITY_NINE_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Tranquility Nine',
  summary: 'a side-view lunar lander descent game over jagged moon terrain',
  world: { w: W, h: H, orientation: 'portrait' },
  // A phone-shaped view following the module down the column, framed high
  // so the rock and its pads are in sight from the top of the drop.
  camera: { mode: 'follow', view: { w: 9, h: 16 }, lookAhead: 0, ease: 0.16, bias: { x: 0, y: 4 } },
  players: { archetypes: ['descent-module'], spawns: [{ x: SPAWN.x, y: SPAWN.centreY + MODULE.h / 2, facing: 1 }] },
  archetypes: {
    'descent-module': {
      role: 'a fragile boxy lunar descent module with four spindly landing legs, a gold foil skirt and one downward bell nozzle',
      tags: ['player', 'lander'], size: MODULE, render: { palette: 4, depth: 20, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.thrustRotate', config: { gravity: 1.4, thrust: 3.5, rotateSpeed: 1.8, maxTilt: Math.PI / 2, ceiling: 0.35, fuelScalar: 'fuel.level' } },
        { use: 'rule.landingJudge', config: { terrain: 'world.heightfield', maxVy: 2.2, maxVx: 1.2, maxTilt: 0.35, grace: 1.4, graceTiltCap: 0.5,
          feetHalf: 0.32, feetDrop: FEET_DROP, basePoints: 50, softPoints: 150, settleTicks: 45, goalSignal: GOAL, parkTicks: 40, flagOverLimit: true } },
        { use: 'core.vitals', config: { lives: 2, hurtTicks: 30, respawnTicks: 30, invulnTicks: 0 } },
      ],
    },
    'landing-pad': {
      role: 'a flat metal landing pad deck with striped edge markers and small corner lights, bolted onto grey moon rock',
      tags: ['pad'], size: { w: 1, h: 0.4 }, render: { palette: 7, depth: 6, anchor: 'bottom', surface: true, facing: 'front' },
      modules: [{ use: 'body.static', config: {} }],
    },
    'debris-shard': {
      role: 'a torn metal panel fragment with gold foil edges, wreckage from a crash',
      tags: ['debris'], size: { w: 0.3, h: 0.26 }, render: { palette: 5, depth: 18, anchor: 'center', facing: 'symmetric' },
      modules: [{ use: 'body.ballistic', config: { gravity: 1.4, lifeTicks: 55, landOnSurfaces: false, leaveTag: 'debris' } }],
    },
  },
  globals,
  // The concept's instruments: score, fuel, lives, and the two speeds (flagged ! over the limit).
  hud: [{ from: 'score', field: 'score' }, { from: 'fuel', field: 'level' }, { from: 'core.vitals', field: 'lives' },
    { from: 'rule.landingJudge', field: 'vs' }, { from: 'rule.landingJudge', field: 'hs' }],
  input: { profile: 'run-jump', labels: { a: 'THRUST' } },
  presentation: {
    projection: 'side',
    backgroundRole: 'a black starfield over a distant grey lunar horizon with a crescent Earth hanging low, empty vacuum sky',
    palette: ['#000000', '#0b0e14', '#cccccc', '#808080', '#ffffff', '#ffd700', '#ffa500', '#00ff88'],
    backgroundPaletteIndex: 1,
  },
};

export const TRANQUILITY_NINE_TEMPLATE: TemplateV1 = {
  id: 'lander/tranquility-nine',
  family: 'lander',
  title: 'Tranquility Nine lunar lander',
  pitch: 'Lunar descent: roll and burn a fragile module onto flat multiplier pads in jagged terrain on one tank of fuel; bank three landings.',
  disclosure: {
    coreVerbs: ['rotate', 'thrust'],
    mechanics: [
      'Constant lunar gravity pulls the module down and there is no air drag: every bit of speed persists until thrust cancels it.',
      'Left and right roll the module in place; the main engine always pushes along its nose, so a tilt turns thrust into sideways motion.',
      'The engine burns a single fixed tank of fuel every moment it fires; rotation is free.',
      'The terrain is a jagged rock profile exactly one screen wide, and the camera follows the module down a short descent column.',
      'Flat landing pads sit in the rock, each showing its score multiplier (x2 to x8) — the narrower the deck, the higher the multiplier; a wide x2 safety deck always lies under the drop column.',
      'A clean landing needs slow vertical and sideways speed, a near-level module and both feet on one deck; the first landing is judged a little more gently.',
      'A clean landing banks the multiplier times your softness and refunds some fuel; then a new field is generated, its decks 15% narrower than the last.',
      'Touching raw rock, or hitting a deck too fast or too tilted, breaks the module apart and costs one of two lives; with the tank empty you can only fall.',
    ],
    controls: 'Left/right rotate the module; hold Thrust (up or the action button) to fire the main engine along the nose.',
    winCondition: 'Bank three clean pad landings to win; a second crash ends the run.',
  },
  music: { mood: 'tense silent weightless precision', genre: 'ambient space synth', tempoBpm: 84,
    instrumentation: ['analog synth pad', 'sine sub bass', 'soft glassy arpeggio', 'sparse electronic pulse'] },
  spec: TRANQUILITY_NINE_SPEC,
  tunables: [
    { path: '/archetypes/descent-module/modules/body.thrustRotate/gravity', min: 1, max: 2 },
    { path: '/archetypes/descent-module/modules/body.thrustRotate/thrust', min: 2.8, max: 4.5 },
    { path: '/archetypes/descent-module/modules/body.thrustRotate/rotateSpeed', min: 1.2, max: 2.6 },
    { path: '/archetypes/descent-module/modules/rule.landingJudge/maxVy', min: 1.6, max: 3 },
    { path: '/archetypes/descent-module/modules/rule.landingJudge/maxVx', min: 0.8, max: 1.6 },
    { path: '/archetypes/descent-module/modules/rule.landingJudge/maxTilt', min: 0.25, max: 0.45 },
    { path: '/archetypes/descent-module/modules/core.vitals/lives', min: 1, max: 4, integer: true },
    { path: '/globals/fuel/capacity', min: 100, max: 220 },
    { path: '/globals/fuel/adjust/1/amount', min: 0, max: 40 },
    { path: '/globals/world.heightfield/jitter', min: 0.6, max: 1.6 },
    { path: '/globals/flow.escalation/curves/0/rate', min: 0.75, max: 1 },
    { path: '/globals/flow.objective/count', min: 2, max: 5, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
  ],
  requirements: { events: ['ignite', 'land', 'refill', 'phase-clear'] },
  signatureModules: ['body.thrustRotate', 'world.heightfield', 'rule.landingJudge'],
  // The module drops from the top of the column: rolling changes its
  // attitude, the engine lights. Landing needs a flown descent, which the
  // solver proof covers (required `land` events), not a fixed native script.
  verbProofs: [
    { verb: 'rotate', input: 'left', ms: 500, expect: { kind: 'state-change', path: 'seats.0.angle' } },
    { verb: 'thrust', input: 'a', ms: 300, expect: { kind: 'event', event: 'ignite' } },
  ],
};
